
import React, { useCallback, useEffect, useState } from "react";
import {
  X,
  FileDigit,
  Plus,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Pencil,
  Save,
  Eye,
  Ticket,
  Hash,
  Link2,
  Clock,
  Search,
  CircleX,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import axios from "axios";
import { domain } from "../../../security";

const initialForm = {
  seriesName: "",
  prefix: "",
  startNumber: 1,
  endNumber: 999999,
  numberLength: 6,
};

const TripTicketNumberSeries = ({ visible, onClose }) => {
  const [seriesList, setSeriesList] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchSeries = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${domain}/api/TripTicketNumberSeries`);
      setSeriesList(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to load or no series number yet."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      fetchSeries();
      setSuccess("");
    }
  }, [visible, fetchSeries]);

  useEffect(() => {
    if (!visible) return;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [visible, saving, onClose]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
    setSuccess("");
  };

  const formatNumber = (number, length = 5, prefix = "") =>
    `${prefix || ""}${Number(number).toString().padStart(length, "0")}`;

  const handleCreate = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const start = Number(form.startNumber);
    const end = Number(form.endNumber);
    const length = Number(form.numberLength);

    if (!form.seriesName.trim()) {
      setError("Please enter a series name.");
      return;
    }

    if (
      !Number.isInteger(start) ||
      !Number.isInteger(end) ||
      start < 1 ||
      end < start
    ) {
      setError("Enter a valid number range. The end must be at least the start.");
      return;
    }

    if (
      !Number.isInteger(length) ||
      length < 1 ||
      length > 10 ||
      length < String(end).length
    ) {
      setError("Number length must be between 1 and 10 and accommodate the end number.");
      return;
    }

    setSaving(true);

    const _data = {
        seriesName: form.seriesName.trim(),
        prefix: form.prefix.trim() || null,
        startNumber: start,
        endNumber: end,
        numberLength: length,
    }

    try {
      await axios.post(
        `${domain}/api/TripTicketNumberSeries`, _data ,
      );

      setSuccess("Trip ticket number series created successfully.");
      setForm(initialForm);
      setShowForm(false);

      await fetchSeries();
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.message ||
          "Failed to create trip ticket number series."
      );
    } finally {
      setSaving(false);
    }
  };

  const [showUpdateForm, setShowUpdateForm] = useState(false)

  const openUpdateForm = (_data) => {
    setShowUpdateForm(true)
    setForm(_data)
  }
  const closeUpdateForm = () => {
    setShowUpdateForm(false)
    setForm(initialForm)
  }

  const handleUpdate = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    setSaving(true);

    const _data = {
      seriesName: form.seriesName.trim(),
      nextNumber: form.nextNumber,
      isActive: form.isActive,
    }

    try {
      await axios.post(
        `${domain}/api/TripTicketNumberSeries/update/${form?.id}`, _data ,
      );

      setSuccess("Trip ticket number series updated successfully.");
      setForm(initialForm);
      setShowForm(false);

      await fetchSeries();
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.message ||
          "Failed to update trip ticket number series."
      );
    } finally {
      setSaving(false);
    }
  };

  const [showViewTicketForm, setShowViewTicketForm] = useState(false)

  const openViewTicketForm = (_data) => {
    setShowViewTicketForm(true)
    setForm(_data)
  }
  const closeViewTicketForm = () => {
    setShowViewTicketForm(false)
    setForm(initialForm)
  }

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/50 p-3 sm:p-6 backdrop-blur-sm"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !saving) onClose?.();
        }}
      >
        <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 sm:px-7">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <FileDigit size={23} />
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-tight text-gray-900">
                  Trip Ticket Number Series
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  Manage your pre-numbered trip ticket forms
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle size={17} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                <CheckCircle2 size={17} className="mt-0.5 shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {/* List heading */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Configured Series
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Review number ranges and their current status.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={fetchSeries}
                  disabled={loading}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw
                    size={15}
                    className={loading ? "animate-spin" : ""}
                  />
                  <span className="hidden sm:inline">Refresh</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForm((prev) => !prev);
                    setError("");
                    setSuccess("");
                    setForm(initialForm)
                  }}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                >
                  {showForm ? <X size={16} /> : <Plus size={16} />}
                  {showForm ? "Cancel" : "New Series"}
                </button>
              </div>
            </div>

            {/* Create form */}
            {showForm && (
              <form
                onSubmit={handleCreate}
                className="mb-6 rounded-xl border border-blue-100 bg-blue-50/40 p-4 sm:p-5"
              >
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-gray-900">
                    Create Number Series
                  </h4>
                  <p className="mt-1 text-xs text-gray-500">
                    Configure the range to match your printed ticket forms.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                      Series Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      maxLength={50}
                      value={form.seriesName}
                      onChange={(e) => updateField("seriesName", e.target.value)}
                      placeholder="e.g. Trip Ticket 2026"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                      Prefix <span className="font-normal text-gray-400">(Optional)</span>
                    </label>
                    <input
                      maxLength={20}
                      value={form.prefix}
                      onChange={(e) => updateField("prefix", e.target.value)}
                      placeholder="e.g. TT-"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                      Number Length
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      required
                      value={form.numberLength}
                      onChange={(e) =>
                        updateField("numberLength", e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                      Start Number
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={form.startNumber}
                      onChange={(e) =>
                        updateField("startNumber", e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                      End Number
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={form.endNumber}
                      onChange={(e) =>
                        updateField("endNumber", e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div className="flex items-end">
                    <div className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2.5">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Number Preview
                      </p>
                      <p className="font-mono text-sm font-bold text-blue-700">
                        {formatNumber(
                          Number(form.startNumber) || 1,
                          Math.min(10, Math.max(1, Number(form.numberLength) || 6)),
                          form.prefix
                        )}
                        <span className="mx-2 text-gray-300">–</span>
                        {formatNumber(
                          Number(form.endNumber) || 999,
                          Math.min(10, Math.max(1, Number(form.numberLength) || 6)),
                          form.prefix
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex flex-col-reverse justify-end gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setForm(initialForm);
                      setError("");
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving && <Loader2 size={16} className="animate-spin" />}
                    {saving ? "Creating..." : "Create Series"}
                  </button>
                </div>
              </form>
            )}

            {/* Series table */}
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Series
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Number Range
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Next Number
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        Manage
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center">
                          <Loader2
                            size={24}
                            className="mx-auto animate-spin text-blue-500"
                          />
                          <p className="mt-2 text-sm text-gray-500">
                            Loading number series...
                          </p>
                        </td>
                      </tr>
                    ) : seriesList.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-14 text-center">
                          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                            <FileDigit size={24} />
                          </div>
                          <p className="mt-3 text-sm font-semibold text-gray-700">
                            No number series yet
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            Create a series to start managing trip ticket numbers.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      seriesList.map((item) => {
                        return (
                          <tr
                            key={item.id}
                            className="transition hover:bg-gray-50/80"
                          >
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-600">
                                  <FileDigit size={18} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {item.seriesName}
                                  </p>
                                  <p className="mt-0.5 text-xs text-gray-400">
                                    ID: {item.id}
                                    {item.prefix ? ` · Prefix: ${item.prefix}` : ""}
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-4">
                              <p className="font-mono text-xs font-semibold text-gray-700">
                                {formatNumber(
                                  item.startNumber,
                                  item.numberLength,
                                  item.prefix
                                )}
                              </p>
                              <p className="font-mono text-xs font-semibold text-gray-700">
                                to{" "}
                                {formatNumber(
                                  item.endNumber,
                                  item.numberLength,
                                  item.prefix
                                )}
                              </p>
                            </td>

                            <td className="px-4 py-4">
                              {item.nextNumber <= item.endNumber ? (
                                <div>
                                  <span className="inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-1 font-mono text-sm font-bold text-blue-700">
                                    {formatNumber(
                                      item.nextNumber,
                                      item.numberLength,
                                      item.prefix
                                    )}
                                  </span>
                                  <p className="mt-1 text-[10px] text-gray-400">
                                    Next to be issued
                                  </p>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  No numbers available
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                                  item?.isActive
                                    ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
                                    : "bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-500/20"
                                }`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    item?.isActive ? "bg-green-500" : "bg-gray-400"
                                  }`}
                                />
                                {item?.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>

                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                {/* Update */}
                                <button
                                  type="button"
                                  onClick={() => openUpdateForm(item)}
                                  disabled={saving}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 transition-all hover:border-green-300 hover:bg-green-100"
                                >
                                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={14} />}
                                  {saving ? "Updating..." : "Update"}
                                </button>

                                {/* View Tickets */}
                                <button
                                  type="button"
                                  onClick={() => openViewTicketForm(item)}
                                  disabled={saving}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition-all hover:border-blue-300 hover:bg-blue-100"
                                >
                                  <Eye size={14} />
                                  View Tickets
                                  <ChevronRight size={13} className="ml-0.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3.5 py-3 text-xs leading-relaxed text-amber-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <p>
                <strong>Reminder:</strong> Set the next number to match your
                first unused printed ticket. Creating a series does not issue a
                ticket. Number reservation should happen when the shipment is
                saved.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-100 bg-white px-5 py-3.5 sm:px-7">
            <p className="hidden text-xs text-gray-400 sm:block">
              POS · Trip Ticket Configuration
            </p>
            <button
              type="button"
              onClick={onClose}
              className="ml-auto flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            >
              Close
              <ChevronRight size={15} className="rotate-90" />
            </button>
          </div>
        </div>
      
        <UpdateModal 
          visible={showUpdateForm} onClose={closeUpdateForm} 
          handleSubmit={handleUpdate}
          form={form} setForm={setForm}
        />
      
        <ViewTicketModal 
          visible={showViewTicketForm} onClose={closeViewTicketForm} 
          form={form} setForm={setForm}
        />

      </div>
    </>
  );
};

const UpdateModal = ({
  visible,
  onClose,
  handleSubmit,
  form,
  setForm,
}) => {
  if (!visible) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const submit = (e) => {
    onClose();
    handleSubmit(e);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-100 p-2 text-blue-600">
              <FileDigit size={22} />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Update Number Series
              </h2>
              <p className="text-sm text-gray-500">
                Edit the series name and status.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit}>
          <div className="space-y-5 px-6 py-6">
            <div>
              <label
                htmlFor="seriesName"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Series Name
              </label>

              <input
                id="seriesName"
                name="seriesName"
                type="text"
                value={form?.seriesName ?? ""}
                onChange={handleChange}
                maxLength={50}
                required
                placeholder="Enter series name"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div>
              <label
                htmlFor="nextNumber"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Next Number
              </label>

              <input
                id="nextNumber"
                name="nextNumber"
                type="number"
                min={form?.startNumber ?? 1}
                max={form?.endNumber}
                step={1}
                value={form?.nextNumber ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nextNumber:
                      e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
                required
                placeholder="Enter next number"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />

              <p className="mt-1 text-xs text-gray-500">
                Allowed range: {form?.startNumber ?? 1} – {form?.endNumber ?? "—"}
              </p>
              
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                <span className="font-semibold">Note:</span> Any ticket numbers with an
                <span className="font-semibold"> Available </span>
                status before the selected Next Number will be marked as
                <span className="font-semibold"> Skipped </span>
                and will no longer be available for use.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-gray-200 p-4">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Series Status
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {form?.isActive
                    ? "This series is active and available for use."
                    : "This series is inactive and unavailable for use."}
                </p>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={!!form?.isActive}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    isActive: !prev.isActive,
                  }))
                }
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  form?.isActive ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form?.isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  form?.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                <span
                  className={`mr-2 h-2 w-2 rounded-full ${
                    form?.isActive ? "bg-green-500" : "bg-gray-400"
                  }`}
                />
                {form?.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
const ViewTicketModal = ({
  visible,
  onClose,
  form,
}) => {
  const [data, setData] = useState([]);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const pageSize = 10;

  const fetchData = useCallback(async () => {
    if (!form?.id) return;

    setLoading(true);

    try {
      const response = await axios.get(
        `${domain}/api/TripTicketNumberSeries/${form.id}`
      );

      setSeries(response.data);
      setData(response.data?.tripTicketNumberSeriesDetails ?? []);
    } catch (err) {
      console.error(
        "Failed to fetch ticket details:",
        err.response?.data ?? err.message
      );
      setData([]);
      setSeries(null);
    } finally {
      setLoading(false);
    }
  }, [form?.id]);

  useEffect(() => {
    if (visible && form?.id) {
      setSearch("");
      setStatusFilter("All");
      setCurrentPage(1);
      fetchData();
    }
  }, [visible, form?.id, fetchData]);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredData = data.filter((ticket) => {
    const matchesSearch =
      (ticket.ticketNumber ?? "").toLowerCase().includes(normalizedSearch) ||
      String(ticket.referenceId ?? "").toLowerCase().includes(normalizedSearch);

    const matchesStatus =
      statusFilter === "All" ||
      ticket.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + pageSize
  );

  const availableCount = data.filter(
    (ticket) => ticket.status?.toLowerCase() === "available"
  ).length;

  const usedCount = data.filter(
    (ticket) => ticket.referenceId != null
  ).length;

  const cancelledCount = data.filter(
    (ticket) => ticket.status?.toLowerCase() === "cancelled"
  ).length;

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "bg-green-50 text-green-700 border-green-200";
      case "used":
      case "consumed":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "reserved":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "cancelled":
      case "void":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const [showUpdateForm, setShowUpdateForm] = useState(false)
  const [selectedData, setSelectedData] = useState(null)

  const openUpdateForm = (_data) => {
    setShowUpdateForm(true)
    setSelectedData(_data)
  }
  const closeUpdateForm = () => {
    setShowUpdateForm(false)
    setSelectedData(null)
  }

  const handleUpdate = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    setLoading(true);

    const _data = {
      status: selectedData?.status,
    }

    try {
      await axios.post(
        `${domain}/api/TripTicketNumberSeries/change-ticket-status/${selectedData?.id}`, _data ,
      );

      setSuccess("Ticket status updated successfully.");

      await fetchData();
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.message ||
          "Failed to update ticket status."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
              <Ticket size={23} />
            </div>

            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Ticket Numbers
              </h2>
              <p className="text-sm text-gray-500">
                Series: {series?.seriesName ?? form?.seriesName ?? "—"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchData}
              disabled={loading}
              title="Refresh tickets"
              className="rounded-lg border border-gray-200 p-2 text-gray-500 transition hover:bg-gray-100 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <RefreshCw size={18} />
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            >
              <X size={21} />
            </button>
          </div>
        </div>

        {/* Series details */}
        <div className="grid grid-cols-2 gap-3 border-b border-gray-100 bg-gray-50/70 p-5 sm:grid-cols-4 sm:px-6">
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500">Total Tickets</p>
            <div className="mt-2 flex items-center gap-2">
              <Hash size={17} className="text-blue-600" />
              <span className="text-xl font-bold text-gray-900">
                {data.length.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500">Available</p>
            <div className="mt-2 flex items-center gap-2">
              <CheckCircle2 size={17} className="text-green-600" />
              <span className="text-xl font-bold text-green-700">
                {availableCount.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500">Referenced</p>
            <div className="mt-2 flex items-center gap-2">
              <Link2 size={17} className="text-blue-600" />
              <span className="text-xl font-bold text-blue-700">
                {usedCount.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500">Cancelled</p>
            <div className="mt-2 flex items-center gap-2">
              <Clock size={17} className="text-red-600" />
              <span className="text-xl font-bold text-red-700">
                {cancelledCount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Search and filters */}
        <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="relative w-full sm:max-w-sm">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search ticket or reference..."
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
          >
            <option value="All">All Statuses</option>
            <option value="Available">Available</option>
            <option value="Skipped">Skipped</option>
            <option value="Used">Used</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <CircleX size={20} className="mt-0.5 shrink-0" />

            <div>
              <p className="font-semibold">Update Failed</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
          >
            <CheckCircle2 size={20} className="mt-0.5 shrink-0" />

            <div>
              <p className="font-semibold">Success</p>
              <p className="mt-1">{success}</p>
            </div>
          </div>
        )}

        {/* Tickets table */}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-3 font-semibold sm:px-6">#</th>
                <th className="px-5 py-3 font-semibold">Ticket Number</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Reference ID</th>
                <th className="px-5 py-3 font-semibold">Manage</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-16 text-center">
                    <Loader2
                      size={28}
                      className="mx-auto animate-spin text-blue-600"
                    />
                    <p className="mt-3 text-sm text-gray-500">
                      Loading ticket numbers...
                    </p>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center">
                    <CircleX
                      size={30}
                      className="mx-auto text-gray-300"
                    />
                    <p className="mt-3 font-medium text-gray-700">
                      No tickets found
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Try changing your search or status filter.
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedData.map((ticket, index) => (
                  <tr
                    key={ticket.id}
                    className="transition hover:bg-blue-50/40"
                  >
                    <td className="px-5 py-3.5 text-gray-400 sm:px-6">
                      {(safePage - 1) * pageSize + index + 1}
                    </td>

                    <td className="px-5 py-3.5">
                      <span className="font-mono font-semibold text-gray-900">
                        {ticket.ticketNumber}
                      </span>
                    </td>

                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusStyle(
                          ticket.status
                        )}`}
                      >
                        <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current" />
                        {ticket.status}
                      </span>
                    </td>

                    <td className="px-5 py-3.5">
                      {ticket.referenceId != null ? (
                        <span className="inline-flex items-center gap-1.5 font-medium text-blue-700">
                          <Link2 size={14} />
                          {ticket.shipmentNumber ?? "—"}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => openUpdateForm(ticket)}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 transition-all hover:border-green-300 hover:bg-green-100"
                      >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={14} />}
                        {loading ? "Updating..." : "Update"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-xs text-gray-500">
            Showing{" "}
            <span className="font-semibold text-gray-700">
              {filteredData.length === 0 ? 0 : startIndex + 1}
            </span>
            {" "}–{" "}
            <span className="font-semibold text-gray-700">
              {Math.min(startIndex + pageSize, filteredData.length)}
            </span>
            {" "}of{" "}
            <span className="font-semibold text-gray-700">
              {filteredData.length.toLocaleString()}
            </span>
            {" "}tickets
          </p>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* First Page */}
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage(1)}
              title="Go to first page"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronsLeft size={15} />
              <span className="hidden sm:inline">First</span>
            </button>

            {/* Previous */}
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={15} />
              Previous
            </button>

            <span className="px-2 text-xs font-medium text-gray-600">
              Page {safePage} of {totalPages}
            </span>

            {/* Next */}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <ChevronRight size={15} />
            </button>

            {/* Last Page */}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Go to last page"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="hidden sm:inline">Last</span>
              <ChevronsRight size={15} />
            </button>
          </div>
        </div>
      </div>

      <UpdateTicketModal 
        visible={showUpdateForm} onClose={closeUpdateForm} 
        handleSubmit={handleUpdate}
        selectedData={selectedData} setSelectedData={setSelectedData}
      />
    </div>
  );
};
const UpdateTicketModal = ({
  visible,
  onClose,
  handleSubmit,
  selectedData,
  setSelectedData,
}) => {
  if (!visible) return null;

  const submit = (e) => {
    onClose();
    handleSubmit(e);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-100 p-2 text-blue-600">
              <FileDigit size={22} />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Update Ticket
              </h2>
              <p className="text-sm text-gray-500">
                Edit status of ticket
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit}>
          <div className="space-y-5 px-6 py-6">
            {/* Ticket Number */}
            <div>
              <label
                htmlFor="ticketNumber"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Ticket Number
              </label>

              <input
                id="ticketNumber"
                type="text"
                value={selectedData?.ticketNumber ?? ""}
                readOnly
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 outline-none"
              />
            </div>

            {/* Status */}
            <div>
              <label
                htmlFor="status"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Status
              </label>

              <select
                id="status"
                name="status"
                value={selectedData?.status ?? "Available"}
                onChange={(e) =>
                  setSelectedData((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
                required
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="Available">Available</option>
                <option value="Skipped">Skipped</option>
                <option value="Used">Used</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <p className="mt-1 text-xs text-gray-500">
                Select the status you want to assign to this ticket.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripTicketNumberSeries;
